import { useCallback, useRef } from 'react'
import { ensureAiChatSession } from '../ai-chat-live-session'
import { useAiChatFiles } from './use-ai-chat-files'
import type { useAiChatDraft } from '../use-ai-chat-draft'
import type { LiveAiChatScope } from '../ai-chat-live-content'
import type { useStartDocumentCollectionAiChatMutation } from '@/graphql/mutations/startDocumentCollectionAiChatMutation.gql.generated'
import type { useStartWorkspaceAiChatMutation } from '@/graphql/mutations/startWorkspaceAiChatMutation.gql.generated'

type LiveFilesInput = {
  scope: LiveAiChatScope
  documentCollectionId?: string
  workspaceId?: string
}
type Mutations = {
  startWorkspaceChat: ReturnType<typeof useStartWorkspaceAiChatMutation>[0]
  startDocumentCollectionChat: ReturnType<
    typeof useStartDocumentCollectionAiChatMutation
  >[0]
}

/** Composer files, creating the chat session when the first file is added. */
export function useAiChatLiveFiles({
  input,
  activeSessionId,
  mutations,
  draft,
  selectSession,
}: {
  input: LiveFilesInput
  activeSessionId?: string
  mutations: Mutations
  draft: Pick<ReturnType<typeof useAiChatDraft>, 'moveToSession'>
  selectSession: (sessionId: string) => void
}) {
  const ensureSessionRef = useRef<() => Promise<string>>(() =>
    Promise.reject(new Error('AI chat is not ready.')),
  )
  const files = useAiChatFiles({
    sessionKey: activeSessionId,
    ensureSession: useCallback(() => ensureSessionRef.current(), []),
  })
  const { moveToSession } = files
  const moveDraft = draft.moveToSession
  ensureSessionRef.current = () =>
    ensureAiChatSession({
      activeSessionId,
      documentCollectionId: input.documentCollectionId,
      scope: input.scope,
      setCreatedSessionId: (sessionId) => {
        moveDraft(sessionId)
        moveToSession(sessionId)
        selectSession(sessionId)
      },
      startDocumentCollectionChat: mutations.startDocumentCollectionChat,
      startWorkspaceChat: mutations.startWorkspaceChat,
      workspaceId: input.workspaceId,
    })

  return files
}

type SubmitPrompt = (
  prompt: string,
  onAccepted?: (sessionId: string) => void,
  decision?: undefined,
  fileIds?: Array<string>,
) => Promise<void>

/** Sends the draft with ready files and clears both once the server accepts. */
export function submitDraftWithFiles({
  draft,
  files,
  submitPrompt,
}: {
  draft: ReturnType<typeof useAiChatDraft>
  files: ReturnType<typeof useAiChatFiles>
  submitPrompt: SubmitPrompt
}) {
  const acknowledgeDraft = draft.captureAcknowledgment()
  const sentKeys = files.readyKeys
  void submitPrompt(
    draft.draft,
    (acceptedSessionId) => {
      acknowledgeDraft(acceptedSessionId)
      files.settle(acceptedSessionId, sentKeys)
    },
    undefined,
    files.readyIds,
  )
}
